import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  MEDIA_CATEGORIES,
  publishVideo,
  publishTrack,
  publishVideoByLink,
  uploadCoverImage,
  isSupportedMediaUrl,
} from '@/lib/media';

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  onPublished: () => void;
}

type ContentTab = 'video' | 'acapella' | 'ad' | 'audio';

const TAB_CONFIG: Record<
  ContentTab,
  {
    label: string;
    icon: string;
    kind: 'video' | 'audio';
    bucketFolder: string;
    accept: string;
    fileLabel: string;
  }
> = {
  video: {
    label: 'Vidéo',
    icon: 'ri-video-line',
    kind: 'video',
    bucketFolder: 'videos',
    accept: 'video/*',
    fileLabel: 'vidéo',
  },
  acapella: {
    label: 'Acapella',
    icon: 'ri-mic-line',
    kind: 'audio',
    bucketFolder: 'audio',
    accept: 'audio/*',
    fileLabel: 'audio',
  },
  ad: {
    label: 'Publicité',
    icon: 'ri-advertisement-line',
    kind: 'video',
    bucketFolder: 'videos',
    accept: 'video/*',
    fileLabel: 'vidéo',
  },
  audio: {
    label: 'Musique',
    icon: 'ri-music-2-line',
    kind: 'audio',
    bucketFolder: 'audio',
    accept: 'audio/*',
    fileLabel: 'audio',
  },
};

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function UploadModal({ open, onClose, onPublished }: UploadModalProps) {
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';
  const [tab, setTab] = useState<ContentTab>('video');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState(MEDIA_CATEGORIES[0]);
  const [artistName, setArtistName] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [linkMode, setLinkMode] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const reset = () => {
    setTab('video');
    setTitle('');
    setCategory(MEDIA_CATEGORIES[0]);
    setArtistName('');
    setLocation('');
    setDescription('');
    setFile(null);
    setCoverFile(null);
    setCoverPreview(null);
    setLinkMode(false);
    setLinkUrl('');
    setError('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleTabChange = (t: ContentTab) => {
    setTab(t);
    setFile(null);
    setLinkMode(false);
    setLinkUrl('');
    setError('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setError('');
  };

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setCoverFile(f);
    if (f) {
      const url = URL.createObjectURL(f);
      setCoverPreview(url);
    } else {
      setCoverPreview(null);
    }
  };

  const uploadCover = async (): Promise<string | null> => {
    if (!coverFile) return null;
    return uploadCoverImage(coverFile);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (tab === 'ad' && !isAdmin) {
      setError('Seul un administrateur peut publier des publicités.');
      return;
    }

    if (!title.trim()) {
      setError('Le titre est obligatoire.');
      return;
    }

    // Publication via lien YouTube / TikTok
    if (isVideoTab && linkMode) {
      const url = linkUrl.trim();
      if (!url) {
        setError('Veuillez coller un lien YouTube ou TikTok.');
        return;
      }
      if (!isSupportedMediaUrl(url)) {
        setError('Le lien doit être une URL YouTube ou TikTok valide.');
        return;
      }

      setLoading(true);
      try {
        const coverUrl = coverFile ? await uploadCover() : null;
        await publishVideoByLink(url, coverUrl, {
          title: title.trim(),
          description: description.trim(),
          category,
          contentType: tab,
          artistName: artistName.trim(),
          location: location.trim(),
        });
        reset();
        onPublished();
        onClose();
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : 'Une erreur est survenue lors de la publication.';
        setError(msg);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!file) {
      setError('Veuillez choisir un fichier à publier.');
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      setError('Le fichier ne doit pas dépasser 500 Mo.');
      return;
    }

    setLoading(true);
    try {
      const coverUrl = await uploadCover();
      const config = TAB_CONFIG[tab];
      if (config.kind === 'video') {
        await publishVideo(file, coverUrl, {
          title: title.trim(),
          description: description.trim(),
          category,
          contentType: tab,
          artistName: artistName.trim(),
          location: location.trim(),
        });
      } else {
        await publishTrack(file, coverUrl, {
          title: title.trim(),
          category,
          contentType: tab,
          artistName: artistName.trim(),
          location: location.trim(),
        });
      }
      reset();
      onPublished();
      onClose();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Une erreur est survenue lors de la publication.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const config = TAB_CONFIG[tab];
  const isVideoTab = config.kind === 'video';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background-950/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative w-full max-w-lg bg-background-50 border border-background-200/70 rounded-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70 shrink-0">
          <h3 className="font-heading font-semibold text-lg text-foreground-50">
            Publier sur Média
          </h3>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center text-foreground-500 hover:text-foreground-50 transition-colors"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-2 gap-1 p-1 mx-5 mt-4 bg-background-100 rounded-lg shrink-0">
          {(Object.keys(TAB_CONFIG) as ContentTab[])
            .filter((t) => t !== 'ad' || isAdmin)
            .map((t) => (
              <button
                key={t}
                onClick={() => handleTabChange(t)}
                className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  tab === t
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-400 hover:text-foreground-50'
                }`}
              >
                <i className={TAB_CONFIG[t].icon} />
                {TAB_CONFIG[t].label}
              </button>
            ))}
        </div>

        {/* Formulaire */}
        <form
          onSubmit={handleSubmit}
          className="p-5 space-y-4 overflow-y-auto"
        >
          {/* Mode fichier / lien (vidéo uniquement) */}
          {isVideoTab && (
            <div className="flex gap-1 p-1 bg-background-100 rounded-lg">
              <button
                type="button"
                onClick={() => {
                  setLinkMode(false);
                  setError('');
                }}
                className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                  !linkMode
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-400 hover:text-foreground-50'
                }`}
              >
                <i className="ri-upload-cloud-2-line mr-1" />
                Fichier
              </button>
              <button
                type="button"
                onClick={() => {
                  setLinkMode(true);
                  setError('');
                }}
                className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                  linkMode
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-400 hover:text-foreground-50'
                }`}
              >
                <i className="ri-link mr-1" />
                Lien YouTube / TikTok
              </button>
            </div>
          )}

          {/* Fichier ou lien */}
          {isVideoTab && linkMode ? (
            <div>
              <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                Lien YouTube ou TikTok *
              </label>
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=… ou https://www.tiktok.com/…"
                className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
              <p className="text-xs text-foreground-400 mt-1.5">
                Collez le lien de votre vidéo YouTube ou TikTok, elle sera publiée directement.
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                Fichier {config.fileLabel} *
              </label>
              {!file ? (
                <label className="flex flex-col items-center justify-center gap-2 py-8 px-4 rounded-lg border-2 border-dashed border-background-300/60 hover:border-primary-400/50 cursor-pointer transition-colors">
                  <i
                    className={`ri-${
                      isVideoTab ? 'video-upload' : 'upload-cloud'
                    }-line text-3xl text-foreground-500`}
                  />
                  <span className="text-sm text-foreground-400 text-center">
                    Cliquez pour choisir un fichier {config.fileLabel}
                  </span>
                  <span className="text-xs text-foreground-500">
                    Max 500 Mo
                  </span>
                  <input
                    type="file"
                    accept={config.accept}
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>
              ) : (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 shrink-0">
                    <i
                      className={`ri-${
                        isVideoTab ? 'video-line' : 'music-2-line'
                      } text-xl text-primary-400`}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-foreground-50 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-foreground-400">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFile(null);
                      setError('');
                    }}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-200/60 text-foreground-500 transition-colors shrink-0"
                    aria-label="Retirer le fichier"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-foreground-300 mb-1.5">
              Titre *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex : Ma nouvelle chanson"
              className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
            />
          </div>

          {/* Catégorie */}
          <div>
            <label className="block text-sm font-medium text-foreground-300 mb-1.5">
              Catégorie culturelle
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 focus:outline-none focus:border-primary-400"
            >
              {MEDIA_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Artiste */}
          <div>
            <label className="block text-sm font-medium text-foreground-300 mb-1.5">
              Nom d&apos;artiste / créateur
            </label>
            <input
              type="text"
              value={artistName}
              onChange={(e) => setArtistName(e.target.value)}
              placeholder="Ex : Kiko Golden Boy"
              className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
            />
          </div>

          {/* Localisation */}
          <div>
            <label className="block text-sm font-medium text-foreground-300 mb-1.5">
              Localisation
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ex : Lomé, Togo"
              className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
            />
          </div>

          {/* Description (vidéo uniquement) */}
          {isVideoTab && (
            <div>
              <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Décrivez votre vidéo en quelques mots…"
                className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
              />
              <p className="text-xs text-foreground-500 mt-1 text-right">
                {description.length}/500
              </p>
            </div>
          )}

          {/* Cover (optionnelle) */}
          <div>
            <label className="block text-sm font-medium text-foreground-300 mb-1.5">
              Image de couverture (optionnelle)
            </label>
            {!coverFile ? (
              <label className="flex flex-col items-center justify-center gap-2 py-6 px-4 rounded-lg border-2 border-dashed border-background-300/60 hover:border-primary-400/50 cursor-pointer transition-colors">
                <i className="ri-image-add-line text-2xl text-foreground-500" />
                <span className="text-sm text-foreground-400 text-center">
                  Cliquez pour ajouter une couverture
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleCoverChange}
                />
              </label>
            ) : (
              <div className="relative rounded-lg overflow-hidden border border-background-200/70">
                <img
                  src={coverPreview || ''}
                  alt="Couverture"
                  className="w-full h-40 object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    setCoverFile(null);
                    setCoverPreview(null);
                  }}
                  className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-foreground-950/70 text-foreground-50 hover:bg-foreground-950 transition-colors"
                  aria-label="Retirer la couverture"
                >
                  <i className="ri-close-line" />
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-accent-100/10 border border-accent-400/20">
              <i className="ri-error-warning-line text-accent-400 mt-0.5 shrink-0" />
              <p className="text-sm text-accent-400">{error}</p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={
              loading ||
              !title.trim() ||
              (isVideoTab && linkMode ? !linkUrl.trim() : !file)
            }
            className="w-full px-4 py-3 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Publication en cours…
              </>
            ) : (
              <>
                <i className="ri-upload-cloud-2-line" />
                Publier
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default UploadModal;